import { displayText } from '../../../../../../terminology/store';
import { useTerminology } from '../../../../../../terminology/TerminologyContext';
import React, { useState, useContext, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createCaseFolder } from '../../../../../services/api';
import ThemeContext from '../../../../../ThemeContext';

const AddFolderModal = ({
  show,
  onHide,
  caseId,
  sessions = [],
  onSuccess,
}) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const [folderName, setFolderName] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setFolderName('');
      setSelectedSessionId('');
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!folderName.trim()) {
      toast.warning('Please enter a folder name', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        name: folderName.trim(),
      };

      // If user selected a session, attach folder to that session
      if (selectedSessionId) {
        payload.session_id = Number(selectedSessionId);
      }

      await createCaseFolder(caseId, payload);

      toast.success('Folder created successfully', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
      });

      setFolderName('');
      setSelectedSessionId('');
      onSuccess?.();
      onHide();
    } catch (error) {
      toast.error(
        `Failed to create folder: ${
          error.response?.data?.message || error.message
        }`,
        {
          style: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
          },
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFolderName('');
    setSelectedSessionId('');
    onHide();
  };

  const getSessionLabel = (session) => {
    const sessionName = session.name || `Session ${session.id}`;
    const sessionType =
      session.displayType || session.normalizedType || session.session_type || 'Session';
    return `${sessionName} - ${sessionType}`;
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header
        style={{
          backgroundColor: theme.colors.primary,
          color: theme.colors.textWhite,
          borderBottom: 'none',
        }}
      >
        <Modal.Title style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
          New Folder
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
        }}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="folderName"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'clamp(9px, 2vw, 10px)',
                fontWeight: 'bold',
              }}
            >
              Folder Name
            </label>
            <input
              type="text"
              id="folderName"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              style={{
                width: '100%',
                padding: 'clamp(6px, 2vw, 8px)',
                fontSize: 'clamp(9px, 2vw, 10px)',
                border: `1px solid ${theme.colors.borderColor}`,
                borderRadius: '5px',
                backgroundColor: theme.colors.bgSecondary,
                color: theme.colors.textPrimary,
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="sessionSelect"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'clamp(9px, 2vw, 10px)',
                fontWeight: 'bold',
              }}
            >{displayText(" Session ")}</label>

            <select
              id="sessionSelect"
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'clamp(6px, 2vw, 8px)',
                fontSize: 'clamp(9px, 2vw, 10px)',
                border: `1px solid ${theme.colors.borderColor}`,
                borderRadius: '5px',
                backgroundColor: theme.colors.bgSecondary,
                color: theme.colors.textPrimary,
              }}
            >
              <option value="">{displayText("Case Level (Not under any session)")}</option>
              {sessions.map((session) => (
                <option key={`${session.id}-${session.normalizedType || session.session_type || 'session'}`} value={session.id}>
                  {getSessionLabel(session)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer
        style={{
          backgroundColor: theme.colors.bgPrimary,
          borderTop: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <button
          onClick={handleClose}
          disabled={isLoading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            backgroundColor: theme.colors.danger,
            color: theme.colors.textWhite,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={isLoading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            background: theme.gradients.primary,
            color: theme.colors.textWhite,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddFolderModal;